/**
 * @module public/js/core/auth/tenantClaimRepair
 * @summary Browser helpers for repairing missing tenant claims.
 */

export const DEFAULT_MISSING_TENANT_MESSAGE =
  "We signed you in, but couldn't load your workspace access. Please try again, and contact your administrator if the issue continues.";
export const MISSING_TENANT_NOT_FOUND_MESSAGE =
  "We signed you in, but couldn't find a workspace for this account. Please contact your administrator.";
export const MISSING_TENANT_AMBIGUOUS_MESSAGE =
  'We signed you in, but this account is linked to multiple workspaces and needs administrator help before it can log in.';

/**
 * Convert a tenant-repair HTTP result into user-facing login copy.
 *
 * @param {number} status - HTTP status code from the repair request.
 * @param {string} [detail=''] - Optional backend detail.
 * @returns {string} Human-friendly login issue message.
 */
export function resolveTenantRepairMessage(status, detail = '') {
  if (status === 404) {
    return MISSING_TENANT_NOT_FOUND_MESSAGE;
  }
  if (status === 409) {
    return MISSING_TENANT_AMBIGUOUS_MESSAGE;
  }

  const normalizedDetail = String(detail || '').trim();
  if (normalizedDetail) {
    return `${DEFAULT_MISSING_TENANT_MESSAGE} (${normalizedDetail})`;
  }

  return DEFAULT_MISSING_TENANT_MESSAGE;
}

/**
 * Ask the backend to repair a missing tenant claim for the signed-in user.
 *
 * @param {{
 *   user: { uid?: string, getIdToken: () => Promise<string> },
 *   endpointUrl: string,
 *   getAppCheckToken?: (forceRefresh?: boolean) => Promise<string|null>,
 *   fetchImpl?: typeof fetch
 * }} params - Repair dependencies.
 * @returns {Promise<{ tenantId: string|null, message: string|null }>} Repair result.
 */
export async function requestTenantClaimRepair({
  user,
  endpointUrl,
  getAppCheckToken: resolveAppCheckToken = async () => null,
  fetchImpl = globalThis.fetch,
}) {
  const uid = String(user?.uid || '').trim();
  if (!uid || typeof fetchImpl !== 'function') {
    return { tenantId: null, message: DEFAULT_MISSING_TENANT_MESSAGE };
  }

  try {
    const idToken = await user.getIdToken();
    const headers = new Headers({
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    });

    const appCheckToken = await resolveAppCheckToken(false);
    if (appCheckToken) {
      headers.set('X-Firebase-AppCheck', appCheckToken);
    }

    const response = await fetchImpl(endpointUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body: JSON.stringify({ uid }),
    });

    const isJson = response.headers?.get?.('content-type')?.includes?.('json');
    const payload = isJson ? await response.json().catch(() => null) : null;
    const detail =
      typeof payload?.error === 'string'
        ? payload.error
        : !isJson
          ? await response.text().catch(() => '')
          : '';

    if (!response.ok) {
      return {
        tenantId: null,
        message: resolveTenantRepairMessage(response.status, detail),
      };
    }

    const tenantId =
      typeof payload?.tenantId === 'string' ? payload.tenantId.trim() : '';
    if (!tenantId) {
      return { tenantId: null, message: DEFAULT_MISSING_TENANT_MESSAGE };
    }

    return { tenantId, message: null };
  } catch (error) {
    console.warn('[tenantClaimRepair] tenant self-repair failed', error);
    return { tenantId: null, message: DEFAULT_MISSING_TENANT_MESSAGE };
  }
}
